import axios from '@axios'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import { ref, watch } from '@vue/composition-api'
import pushStateFiltersParams from '@/helpers/pushStateFiltersParams'
import store from '@/store'
import { DEFAULT_PER_PAGE, PER_PAGE_OPTIONS } from '@/constants/base'

export default function useReportsView(root) {
  // Use toast
  const toast = useToast()

  const refReportsViewList = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'created_by', label: 'created by', sortable: true },
    { key: 'created_at', label: 'created at', sortable: true },
    { key: 'actions' },
  ]
  const defaultPerPage = Number(localStorage.getItem('reportsViewPerPage'))
  const perPage = ref(defaultPerPage || DEFAULT_PER_PAGE)
  const totalItems = ref(0)
  const currentPage = ref(1)
  const perPageOptions = PER_PAGE_OPTIONS
  const searchQuery = ref('')
  const sortBy = ref('created_at')
  const isSortDirDesc = ref(true)
  let debounceTimeout = ref(null)

  const dataMeta = ref({
    from: 0,
    to: 0,
    of: 0,
  })

  const refetchData = () => {
    refReportsViewList.value.refresh()
  }
  watch([currentPage, perPage, searchQuery],
    ([currentPageVal, perPageVal, searchQueryVal],
      [currentPagePrevVal, perPagePrevVal, searchQueryPrevVal]) => {
      if (currentPageVal !== currentPagePrevVal) {
        pushStateFiltersParams(root, 'admin-reports-view-list', 'currentPage', currentPageVal)
      }
      if (perPageVal !== perPagePrevVal) {
        pushStateFiltersParams(root, 'admin-reports-view-list', 'perPage', perPageVal)
      }
      if (searchQueryVal !== searchQueryPrevVal) {
        clearTimeout(debounceTimeout)
        debounceTimeout = setTimeout(() => {
          pushStateFiltersParams(root, 'admin-reports-view-list', 'searchQuery', searchQueryVal)
        }, 250)
      }
    })

  const fetchReportViews = (ctx, callback) => {
    const payloadData = {
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
    }
    if (searchQuery.value) payloadData.name = searchQuery.value

    store
      .dispatch('app-reports/fetchReportViews', payloadData)
      .then(response => {
        const { data } = response.data
        const { total, from, to } = response.data.meta
        callback(data)
        totalItems.value = total
        dataMeta.value = {
          from,
          to,
          of: total,
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error fetching Reports Views' list",
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchReportViewsList = () => axios
    .get('/auth/report-views/')
    .then(response => response.data.data)
    .catch(() => toast({
      component: ToastificationContent,
      props: {
        title: 'Error fetching Report View item',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    }))
  const fetchReportView = ID => axios
    .get(`/auth/report-views/${ID}`)
    .then(response => response.data.data)
    .catch(() => toast({
      component: ToastificationContent,
      props: {
        title: 'Error fetching Report View item',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    }))

  const postReportView = queryParams => axios
    .post('/auth/report-views', queryParams)
    .then(response => {
      toast({
        component: ToastificationContent,
        props: {
          title: 'New report view item has created',
          icon: 'ThumbsUpIcon',
          variant: 'info',
        },
      })
      return response.data.data
    })
    .catch(() => toast({
      component: ToastificationContent,
      props: {
        title: 'Error creating report view item',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    }))

  const updateReportView = queryParams => axios
    .put(`/auth/report-views/${queryParams.id}`, queryParams)
    .then(response => {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Report view was successfully updated',
          icon: 'ThumbsUpIcon',
          variant: 'info',
        },
      })
      return response.data.data.id
    })
    .catch(() => toast({
      component: ToastificationContent,
      props: {
        title: 'Error updating report view',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    }))

  const deleteReportView = ID => axios
    .delete(`auth/report-views/${ID}`)
    .then(() => {
      refetchData()
      toast({
        component: ToastificationContent,
        props: {
          title: 'Success deleting report view item',
          icon: 'Trash2Icon',
          variant: 'info',
        },
      })
    })
    .catch(() => {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Error deleting report view item',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    })

  const executeReportView = queryParams => axios
    .post('/auth/report-views/execute', queryParams)
    .then(response => response.data.result)
    .catch(() => toast({
      component: ToastificationContent,
      props: {
        title: 'Error executing report view',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    }))

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  return {
    tableColumns,
    perPage,
    currentPage,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    totalItems,
    isSortDirDesc,
    refReportsViewList,
    fetchReportViews,
    fetchReportViewsList,
    fetchReportView,
    postReportView,
    updateReportView,
    deleteReportView,
    executeReportView,

    refetchData,
  }
}
